import { useState } from "react";
import { SearchBox } from "@mapbox/search-js-react";

interface AddressDetails {
  streetNumber: string;
  streetName: string;
  zipcode: string;
  city: string;
  country: string;
  fullAddress: string;
  latitude?: number;
  longitude?: number;
}

const MapboxSearchBox = () => {
  const [selectedAddress, setSelectedAddress] = useState<AddressDetails | null>(
    null
  );

  // Get the Mapbox access token from environment variables using bracket notation
  const accessToken = import.meta.env["VITE_MAPBOX_ACCESS_TOKEN"] as string;

  // Handle the selection of an address
  const handleRetrieve = (response: any) => {
    console.log("Complete response:", response); // For debugging
    const feature = response.features[0];

    if (!feature) return;

    // Extract address components
    const properties = feature.properties;
    const context = properties.context || {};

    console.log("Properties:", properties);
    console.log("Context:", context);

    // Get street name and number from context.address
    const addressContext = context.address || {};
    let streetName = addressContext.street_name || "";
    let streetNumber = addressContext.address_number || "";

    // Fallback: If street name and number are not available in context.address,
    // try to extract them from the full address
    if (!streetName || !streetNumber) {
      const fullAddress = feature.place_name || properties.full_address || "";
      const addressParts = fullAddress.split(",")[0].trim(); // Get the part before the first comma

      // Try to match a pattern like "Streetname Number"
      const match = addressParts.match(/^(.*?)\s+(\d+[\w]*)$/);
      if (match) {
        if (!streetName) streetName = match[1].trim();
        if (!streetNumber) streetNumber = match[2].trim();
      }
    }

    // Create the address details object
    const addressDetails: AddressDetails = {
      streetNumber,
      streetName,
      zipcode: context.postcode?.name || "",
      city: context.place?.name || "",
      country: context.country?.name || "Deutschland",
      fullAddress: feature.place_name || properties.full_address || "",
      latitude: feature.geometry.coordinates[1],
      longitude: feature.geometry.coordinates[0],
    };

    // Update the state with the selected address
    setSelectedAddress(addressDetails);

    console.log("Selected address:", addressDetails);
  };

  return (
    <div className="rounded-lg bg-white p-4 shadow-sm dark:bg-gray-800">
      <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
        Mapbox SearchBox Test
      </h2>

      <div className="mb-6">
        <label
          htmlFor="mapbox-search"
          className="mb-1 block text-sm font-medium text-gray-900 dark:text-white"
        >
          Adresse
        </label>

        <div id="mapbox-search" className="w-full">
          <SearchBox
            accessToken={accessToken}
            options={{
              language: "de",
              country: "DE",
              types: "address",
            }}
            onRetrieve={handleRetrieve}
            value=""
            placeholder="Geben Sie eine Adresse ein"
          />
        </div>
      </div>

      {/* Display the selected address details */}
      {selectedAddress && (
        <div className="mt-6 rounded-lg bg-gray-50 p-4 dark:bg-gray-700">
          <h3 className="mb-2 text-base font-medium text-gray-900 dark:text-white">
            Selected Address Details:
          </h3>
          <div className="grid grid-cols-1 gap-2 text-sm">
            <p>
              <strong>Full Address:</strong> {selectedAddress.fullAddress}
            </p>
            <p>
              <strong>Street:</strong> {selectedAddress.streetName}
            </p>
            <p>
              <strong>Number:</strong> {selectedAddress.streetNumber}
            </p>
            <p>
              <strong>City:</strong> {selectedAddress.city}
            </p>
            <p>
              <strong>Zipcode:</strong> {selectedAddress.zipcode}
            </p>
            <p>
              <strong>Country:</strong> {selectedAddress.country}
            </p>
            <p>
              <strong>Coordinates:</strong> {selectedAddress.latitude},{" "}
              {selectedAddress.longitude}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapboxSearchBox;
